import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import {
  CheckCircle, Star, Gift, Shield, Users, User, Heart,
  Sparkles, ArrowRight, TrendingUp, Calculator, Wallet, Globe,
  Plane, Award, BarChart3
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Plan Data ────────────────────────────────────────── */
const PLANS = {
  individual: {
    label: "Individual", subtitle: "Solo Explorer", icon: User,
    tiers: [
      { name: "Base", monthly: 3000, corpus: 36000, maturity: 39600, bonus: 10 },
      { name: "Mid", monthly: 5000, corpus: 60000, maturity: 66600, bonus: 11 },
      { name: "Premium", monthly: 10000, corpus: 120000, maturity: 134400, bonus: 12 },
    ],
  },
  couple: {
    label: "Couple", subtitle: "Travel Together", icon: Heart,
    tiers: [
      { name: "Base", monthly: 5000, corpus: 60000, maturity: 66000, bonus: 10 },
      { name: "Mid", monthly: 10000, corpus: 120000, maturity: 133200, bonus: 11 },
      { name: "Premium", monthly: 15000, corpus: 180000, maturity: 201600, bonus: 12 },
    ],
  },
  family: {
    label: "Family", subtitle: "Most Popular", icon: Users, popular: true,
    tiers: [
      { name: "Base", monthly: 10000, corpus: 120000, maturity: 133200, bonus: 11 },
      { name: "Mid", monthly: 15000, corpus: 180000, maturity: 201600, bonus: 12 },
      { name: "Premium", monthly: 20000, corpus: 240000, maturity: 276000, bonus: 15 },
    ],
  },
};

const PERKS = [
  { tier: "mid", label: "International eSIM", desc: "Stay connected globally", icon: Globe },
  { tier: "mid", label: "Uber Travel Vouchers", desc: "Airport transfers sorted", icon: Award },
  { tier: "premium", label: "DNO Travel Kit", desc: "Passport holder, luggage tag, neck pillow & adaptor", icon: Gift },
  { tier: "premium", label: "Complimentary Stay", desc: "1 night stay or shopping credits", icon: Star },
];

/* ── SIP Calculator Logic (11% p.a. compounding) ─────── */
function calcSIP(monthly, months) {
  const annualRate = 0.11;
  const r = annualRate / 12;
  // FV = P × [{(1+r)^n - 1} / r] × (1+r)
  const pow = Math.pow(1 + r, months);
  const fv = monthly * ((pow - 1) / r) * (1 + r);
  const totalInvested = monthly * months;
  const returns = fv - totalInvested;
  return { maturity: Math.round(fv), invested: totalInvested, returns: Math.round(returns) };
}

/* ── Component ───────────────────────────────────────── */
const SafarSamridhiPage = () => {
  const [selectedType, setSelectedType] = useState("family");
  const [selectedTier, setSelectedTier] = useState("Premium");
  const [form, setForm] = useState({ name: "", email: "", mobile: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  // Calculator state
  const [calcMonthly, setCalcMonthly] = useState(10000);
  const [calcMonths, setCalcMonths] = useState(12);

  const calcResult = useMemo(() => calcSIP(calcMonthly, calcMonths), [calcMonthly, calcMonths]);

  // Comparison chart data for all tenures
  const chartData = useMemo(() => {
    return [12, 18, 24].map((m) => {
      const r = calcSIP(calcMonthly, m);
      return { months: m, ...r };
    });
  }, [calcMonthly]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = true;
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = true;
    if (!form.mobile.trim() || !/^[6-9]\d{9}$/.test(form.mobile.replace(/\s/g, ""))) e.mobile = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/sip-leads`, {
        name: form.name.trim(), email: form.email.trim(), mobile: form.mobile.trim(),
        plan_type: selectedType, plan_tier: selectedTier.toLowerCase(), message: form.message,
      });
      setSubmitted(true);
      toast.success("Thank you! Our team will contact you shortly.");
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };

  const plan = PLANS[selectedType];
  const tier = plan.tiers.find((t) => t.name === selectedTier) || plan.tiers[2];

  return (
    <div className="bg-slate-950 text-white" data-testid="safar-samridhi-page">

      {/* ─── HERO ─── */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-emerald-950/40 to-slate-950" />
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-teal-500/5 rounded-full blur-3xl" />
        </div>
        <div className="container-custom relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-5 py-2 mb-6">
              <TrendingUp className="h-4 w-4 text-emerald-400" />
              <span className="text-emerald-300 text-sm font-medium tracking-wide">Travel Investment Plan</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-5 tracking-tight">
              <span className="text-white">Safar</span>{" "}
              <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">Samridhi</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-xl mx-auto mb-8 leading-relaxed">
              Save monthly. Watch it grow. Redeem for the journey of a lifetime.
              Your dream vacation is now an investment.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="#calculator">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-8 py-3 text-base rounded-xl" data-testid="view-plans-btn">
                  <Calculator className="h-4 w-4 mr-2" /> Calculate Returns
                </Button>
              </a>
              <a href="#enroll">
                <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 px-8 py-3 text-base rounded-xl">
                  Enroll Now <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </a>
            </div>
          </motion.div>

          {/* Quick stats */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mt-16 grid grid-cols-3 gap-4 max-w-lg mx-auto">
            {[
              { val: "12–24", label: "Month Plans" },
              { val: "15%", label: "Max Bonus" },
              { val: "₹3K", label: "Starts From" },
            ].map((s) => (
              <div key={s.label} className="text-center p-4 bg-slate-900/50 border border-slate-800 rounded-xl">
                <p className="text-xl font-bold text-emerald-400">{s.val}</p>
                <p className="text-xs text-slate-400 mt-1">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section className="py-20 bg-slate-900/50">
        <div className="container-custom">
          <h2 className="text-2xl font-bold text-center text-white mb-12">How Safar Samridhi Works</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { step: "01", title: "Pick Your Plan", desc: "Individual, Couple or Family — match your travel goals", numClass: "text-emerald-500/20" },
              { step: "02", title: "Save Monthly", desc: "Auto-debit a fixed amount every month for 12–24 months", numClass: "text-teal-500/20" },
              { step: "03", title: "Watch It Grow", desc: "Your corpus earns bonus travel credits at maturity", numClass: "text-cyan-500/20" },
              { step: "04", title: "Travel Big", desc: "Redeem for any DNO package — flights, hotels, experiences", numClass: "text-emerald-500/20" },
            ].map((item) => (
              <motion.div key={item.step} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center">
                <div className={`text-4xl font-black ${item.numClass} mb-2`}>{item.step}</div>
                <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                <p className="text-sm text-slate-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CALCULATOR ─── */}
      <section id="calculator" className="py-20 scroll-mt-20">
        <div className="container-custom max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-1.5 mb-4">
                <Calculator className="h-3.5 w-3.5 text-emerald-400" />
                <span className="text-emerald-300 text-xs font-medium uppercase tracking-wider">Returns Calculator</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">See Your Travel Savings Grow</h2>
              <p className="text-slate-400 text-sm">Plan your monthly savings and see indicative maturity value</p>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8" data-testid="sip-calculator">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Left — Inputs */}
                <div className="space-y-8">
                  {/* Monthly Amount */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-slate-300 text-sm">Monthly Savings</Label>
                      <div className="flex items-center bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
                        <span className="text-slate-400 text-sm pl-3">₹</span>
                        <Input
                          type="number"
                          min={3000}
                          max={50000}
                          step={1000}
                          value={calcMonthly}
                          onChange={(e) => {
                            const v = parseInt(e.target.value) || 3000;
                            setCalcMonthly(Math.min(50000, Math.max(3000, v)));
                          }}
                          className="w-24 border-0 bg-transparent text-white text-right font-semibold focus-visible:ring-0 focus-visible:ring-offset-0"
                          data-testid="calc-amount-input"
                        />
                      </div>
                    </div>
                    <Slider
                      value={[calcMonthly]}
                      onValueChange={(v) => setCalcMonthly(v[0])}
                      min={3000}
                      max={50000}
                      step={1000}
                      className="[&_[role=slider]]:bg-emerald-500 [&_[role=slider]]:border-emerald-400 [&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_.range]:bg-emerald-500"
                      data-testid="calc-amount-slider"
                    />
                    <div className="flex justify-between text-xs text-slate-400 mt-2">
                      <span>₹3,000</span>
                      <span>₹50,000</span>
                    </div>
                  </div>

                  {/* Tenure */}
                  <div>
                    <Label className="text-slate-300 text-sm mb-3 block">Investment Tenure</Label>
                    <div className="grid grid-cols-3 gap-3">
                      {[12, 18, 24].map((m) => (
                        <button
                          key={m}
                          onClick={() => setCalcMonths(m)}
                          className={`py-3 rounded-xl font-semibold text-sm transition-all ${
                            calcMonths === m
                              ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/25"
                              : "bg-slate-800 text-slate-400 border border-slate-700 hover:border-emerald-500/30"
                          }`}
                          data-testid={`calc-months-${m}`}
                        >
                          {m} Months
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right — Results */}
                <div className="flex flex-col justify-center">
                  <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-2xl p-6 space-y-4" data-testid="calc-results">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400 text-sm">Total Investment</span>
                      <span className="text-white font-semibold text-lg" data-testid="calc-invested">₹{calcResult.invested.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400 text-sm">Bonus Travel Credits</span>
                      <span className="text-emerald-400 font-semibold text-lg" data-testid="calc-returns">+ ₹{calcResult.returns.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="border-t border-emerald-500/20 pt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-white font-medium">Maturity Value</span>
                        <span className="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent" data-testid="calc-maturity">
                          ₹{calcResult.maturity.toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>

                    {/* Visual bar */}
                    <div className="pt-2">
                      <div className="h-3 rounded-full bg-slate-800 overflow-hidden flex">
                        <div
                          className="bg-slate-600 rounded-l-full transition-all duration-500"
                          style={{ width: `${(calcResult.invested / calcResult.maturity) * 100}%` }}
                        />
                        <div
                          className="bg-gradient-to-r from-emerald-500 to-teal-400 rounded-r-full transition-all duration-500"
                          style={{ width: `${(calcResult.returns / calcResult.maturity) * 100}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs mt-1.5">
                        <span className="text-slate-400">Your Savings</span>
                        <span className="text-emerald-400">Bonus Credits</span>
                      </div>
                    </div>
                  </div>

                  <a href="#enroll" className="mt-4">
                    <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3 rounded-xl">
                      Start Saving <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </a>
                  <p className="text-xs text-slate-400 text-center mt-2">*Indicative values. Actual returns may vary.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── COMPARISON CHART ─── */}
      <section className="py-16 bg-slate-900/30">
        <div className="container-custom max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-1.5 mb-4">
                <BarChart3 className="h-3.5 w-3.5 text-emerald-400" />
                <span className="text-emerald-300 text-xs font-medium uppercase tracking-wider">Growth Comparison</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">See How Your Savings Grow Over Time</h2>
              <p className="text-slate-400 text-sm">Saving ₹{calcMonthly.toLocaleString("en-IN")}/month across different tenures</p>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8" data-testid="sip-comparison-chart">
              <div className="space-y-6">
                {chartData.map((d) => {
                  const maxMaturity = chartData[chartData.length - 1].maturity;
                  const investedPct = (d.invested / maxMaturity) * 100;
                  const returnsPct = (d.returns / maxMaturity) * 100;
                  const isSelected = d.months === calcMonths;
                  return (
                    <div key={d.months} className={`rounded-xl p-4 transition-all ${isSelected ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-slate-800/50 border border-transparent"}`} data-testid={`chart-bar-${d.months}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className={`text-sm font-bold ${isSelected ? "text-emerald-400" : "text-slate-300"}`}>{d.months} Months</span>
                          {isSelected && <span className="text-[10px] bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full font-semibold">Selected</span>}
                        </div>
                        <span className={`text-lg font-bold ${isSelected ? "text-emerald-400" : "text-white"}`}>₹{d.maturity.toLocaleString("en-IN")}</span>
                      </div>
                      <div className="h-4 rounded-full bg-slate-700/50 overflow-hidden flex">
                        <div
                          className="bg-slate-500 transition-all duration-700 ease-out rounded-l-full"
                          style={{ width: `${investedPct}%` }}
                        />
                        <div
                          className={`transition-all duration-700 ease-out rounded-r-full ${isSelected ? "bg-gradient-to-r from-emerald-500 to-teal-400" : "bg-emerald-600/60"}`}
                          style={{ width: `${returnsPct}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-2 text-xs">
                        <span className="text-slate-400">Invested: ₹{d.invested.toLocaleString("en-IN")}</span>
                        <span className={isSelected ? "text-emerald-400" : "text-slate-400"}>Bonus: + ₹{d.returns.toLocaleString("en-IN")}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 mt-6 pt-4 border-t border-slate-800">
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-slate-500" /><span className="text-xs text-slate-400">Your Savings</span></div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-emerald-500" /><span className="text-xs text-slate-400">Bonus Credits</span></div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── PLANS ─── */}
      <section id="plans" className="py-20 bg-slate-900/50 scroll-mt-20">
        <div className="container-custom">
          <h2 className="text-2xl font-bold text-center text-white mb-3">Membership Plans</h2>
          <p className="text-center text-slate-400 mb-8 text-sm">Choose the plan that fits your travel vision</p>

          {/* Type selector */}
          <div className="flex justify-center gap-3 mb-10">
            {Object.entries(PLANS).map(([key, p]) => {
              const Icon = p.icon;
              return (
                <button
                  key={key}
                  onClick={() => { setSelectedType(key); setSelectedTier("Premium"); }}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${
                    selectedType === key
                      ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/25"
                      : "bg-slate-800 text-slate-400 border border-slate-700 hover:border-emerald-500/30"
                  }`}
                  data-testid={`plan-type-${key}`}
                >
                  <Icon size={16} />
                  {p.label}
                  {p.popular && <span className="text-[10px] bg-emerald-400/20 text-emerald-300 px-1.5 py-0.5 rounded-full font-semibold ml-1">Popular</span>}
                </button>
              );
            })}
          </div>

          {/* Tier cards */}
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {plan.tiers.map((t) => (
              <motion.div key={t.name} layout>
                <Card
                  className={`cursor-pointer transition-all relative overflow-hidden bg-slate-900 ${
                    selectedTier === t.name
                      ? "border-2 border-emerald-500 shadow-xl shadow-emerald-500/10 ring-1 ring-emerald-500/20"
                      : "border border-slate-800 hover:border-slate-700"
                  }`}
                  onClick={() => setSelectedTier(t.name)}
                  data-testid={`tier-${t.name.toLowerCase()}`}
                >
                  {t.name === "Premium" && (
                    <div className="absolute top-0 right-0 bg-emerald-500 text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl">
                      Best Value
                    </div>
                  )}
                  <CardContent className="pt-6 text-center">
                    <h3 className="font-bold text-lg text-white mb-1">{t.name}</h3>
                    <p className="text-3xl font-bold text-emerald-400 mb-0.5">
                      ₹{t.monthly.toLocaleString()}<span className="text-sm font-normal text-slate-400">/mo</span>
                    </p>
                    <div className="my-4 space-y-2 text-sm">
                      <div className="flex justify-between px-2">
                        <span className="text-slate-400">12-Month Corpus</span>
                        <span className="font-semibold text-slate-300">₹{t.corpus.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between px-2">
                        <span className="text-slate-400">Bonus</span>
                        <span className="font-semibold text-emerald-400">+{t.bonus}%</span>
                      </div>
                      <div className="flex justify-between px-2 pt-2 border-t border-dashed border-slate-700">
                        <span className="text-slate-300 font-medium">Maturity</span>
                        <span className="font-bold text-emerald-300 text-base">₹{t.maturity.toLocaleString()}</span>
                      </div>
                    </div>
                    {selectedTier === t.name && <CheckCircle className="h-5 w-5 text-emerald-500 mx-auto" />}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PERKS ─── */}
      <section className="py-16">
        <div className="container-custom">
          <h2 className="text-2xl font-bold text-center text-white mb-8">Exclusive Member Perks</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {PERKS.map((perk) => {
              const Icon = perk.icon;
              return (
                <div key={perk.label} className="bg-slate-900 border border-slate-800 rounded-xl p-5 hover:border-emerald-500/30 transition-colors">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className="h-4 w-4 text-emerald-400" />
                    <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${
                      perk.tier === "premium" ? "bg-emerald-500/10 text-emerald-400" : "bg-slate-800 text-slate-400"
                    }`}>
                      {perk.tier}+
                    </span>
                  </div>
                  <h3 className="font-semibold text-white text-sm mb-1">{perk.label}</h3>
                  <p className="text-xs text-slate-400">{perk.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── REFERRAL ─── */}
      <section className="py-12 bg-slate-900/50">
        <div className="container-custom max-w-2xl text-center">
          <Gift className="h-7 w-7 text-emerald-400 mx-auto mb-3" />
          <h2 className="text-2xl font-bold text-white mb-2">Refer &amp; Earn</h2>
          <p className="text-slate-400 text-sm mb-6">Share the journey — earn bonus travel credits for every referral</p>
          <div className="flex justify-center gap-6">
            {[
              { tier: "Base Plan", credit: 1000 },
              { tier: "Mid & Premium", credit: 2500 },
            ].map((r) => (
              <div key={r.tier} className="bg-slate-900 border border-slate-800 rounded-xl px-6 py-4">
                <p className="text-xs text-slate-400">{r.tier}</p>
                <p className="text-xl font-bold text-emerald-400">₹{r.credit.toLocaleString()}</p>
                <p className="text-[10px] text-slate-400">travel credits</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── ENROLLMENT FORM ─── */}
      <section id="enroll" className="py-20 scroll-mt-20">
        <div className="container-custom max-w-lg">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-2xl font-bold text-center text-white mb-2">Start Your Journey</h2>
            <p className="text-center text-slate-400 mb-1 text-sm">
              Selected: <span className="text-emerald-400 font-medium">{plan.label} — {selectedTier}</span>
            </p>
            <p className="text-center text-slate-400 text-xs mb-8">₹{tier.monthly.toLocaleString()}/mo &rarr; ₹{tier.maturity.toLocaleString()} maturity</p>

            {submitted ? (
              <div className="bg-slate-900 border border-emerald-500/30 rounded-2xl p-8 text-center" data-testid="sip-success">
                <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">You&apos;re In!</h3>
                <p className="text-slate-400 mb-6 text-sm">Our team will reach out within 24 hours to finalize your enrollment.</p>
                <Button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", mobile: "", message: "" }); }} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                  Submit Another Enquiry
                </Button>
              </div>
            ) : (
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6" data-testid="sip-form-card">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="text-slate-400 text-sm">Full Name *</Label>
                    <Input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 ${errors.name ? "border-red-500" : ""}`} data-testid="sip-name" />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Email *</Label>
                    <Input type="email" placeholder="you@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 ${errors.email ? "border-red-500" : ""}`} data-testid="sip-email" />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Mobile *</Label>
                    <Input type="tel" placeholder="9876543210" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className={`bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 ${errors.mobile ? "border-red-500" : ""}`} data-testid="sip-mobile" />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Message (Optional)</Label>
                    <Textarea placeholder="Questions or preferences..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" data-testid="sip-message" />
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold h-11 rounded-xl" data-testid="sip-submit">
                    {submitting ? "Submitting..." : "Enroll Now"}
                  </Button>
                  <p className="text-[10px] text-center text-slate-400 flex items-center justify-center gap-1">
                    <Shield className="h-3 w-3" /> Your information is secure and never shared.
                  </p>
                </form>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* ─── TERMS ─── */}
      <section className="py-10 bg-slate-900/30 border-t border-slate-800">
        <div className="container-custom max-w-3xl">
          <h3 className="font-semibold text-slate-400 mb-3 text-xs uppercase tracking-wider">Terms &amp; Conditions</h3>
          <ul className="text-xs text-slate-400 space-y-1 list-disc list-inside">
            <li>Minimum 12-month commitment required</li>
            <li>Pre-closure subject to enrollment agreement terms</li>
            <li>All returns are in the form of travel credits, redeemable exclusively through DNO services</li>
            <li>Credits are non-transferable and non-encashable</li>
            <li>Subject to enrollment agreement terms</li>
          </ul>
          <p className="text-[10px] text-slate-500 mt-4">DNO EVENTS &amp; DMC PRIVATE LIMITED</p>
        </div>
      </section>
    </div>
  );
};

export default SafarSamridhiPage;
